import { CartAdd } from "@lazarentertainment/web-design-system/components/iconOutline"
import { FireSolid } from "@lazarentertainment/web-design-system/components/iconSolid"
import { Truncate } from "@lazarentertainment/web-design-system/components/truncate"
import ImagePlaceholder from "assets/icons/imagePlaceholder.svg"
import { Badge } from "client/designSystem/Badge"
import { Button } from "client/designSystem/Button"

import { Price } from "../ui/priceComponents/Price"
import { SoldOut } from "../ui/priceComponents/SoldOut"

import styles from "./ProductTile.module.css"

export type ProductTileProps = {
  title: string
  price: string
  priceCurrencyCode: string

  imageUrl: string
  featured?: boolean
  soldOut?: boolean
  addToCartLoading?: boolean
  onClick: () => void
  onAddToCartClick?: () => void
  onBuyNowClick: () => void
}

export const ProductTile = ({
  title,
  price,
  priceCurrencyCode,
  imageUrl,
  featured = false,
  soldOut = false,
  addToCartLoading = false,
  onClick,
  onAddToCartClick,
  onBuyNowClick,
}: ProductTileProps) => {
  const imageWidthPx = featured ? 120 : 100
  const imageHeightPx = featured ? 150 : 100

  return (
    <div className={styles.productTile} data-featured={featured}>
      <button
        type="button"
        onClick={onClick}
        className={styles.productTileClickTarget}
        aria-label="View details"
      />
      <div
        className={styles.productTileImageContainer}
        data-featured={featured}
      >
        {imageUrl ? (
          <img
            className={styles.productTileImage}
            src={imageUrl}
            alt={title}
            width={imageWidthPx}
            height={imageHeightPx}
          />
        ) : (
          <ImagePlaceholder />
        )}
        {featured && (
          <Badge
            className={styles.productTileFeaturedBadge}
            variant="rose"
            icon={FireSolid}
          >
            Featured
          </Badge>
        )}
      </div>
      <div className={styles.productTileRightSection}>
        <Truncate
          lineCount={2}
          className={`${styles.productTileTitle} title-sm`}
        >
          {title}
        </Truncate>
        <div className={styles.productTileBottomRow}>
          {soldOut ? (
            <SoldOut size="sm" />
          ) : (
            <Price
              size={featured ? "lg" : "sm"}
              amount={price}
              currencyCode={priceCurrencyCode}
              showCurrencyCode={!featured}
              variant={featured ? "featured" : "default"}
            />
          )}
          {!soldOut && (
            <div className={styles.productTileActionRow}>
              <Button
                icon={CartAdd}
                variant="secondary"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation() // So the outer button isn't pressed
                  if (!addToCartLoading) {
                    onAddToCartClick()
                  }
                }}
                disabled={onAddToCartClick === undefined}
                className={styles.productTileAddButton}
                data-robot="product-tile-add-to-cart"
                loading={addToCartLoading}
              >
                Add
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation() // So the outer button isn't pressed
                  onBuyNowClick()
                }}
                data-robot="product-tile-buy-now"
              >
                Buy now
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
